'use client';

import React, { useRef, useState, useMemo, useCallback } from 'react';
import { Box, Typography, CohortStatusLabel } from 'src/components';
import type { CohortStatus } from 'src/api/redux/providerCohorts';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faFolderBookmark,
  faCaretDown,
  faBookmark,
} from '@fortawesome/pro-solid-svg-icons';
import { faGlobe } from '@fortawesome/pro-regular-svg-icons';
import { useClickOutside } from 'src/hooks/common/useClickOutside';
import useTableFilterSelections, { extractBaseId } from 'src/hooks/filters/useTableFilterSelections';
import {
  useCodeSetSelectorData,
  CodeSetItemRow,
  CodeSetSelectorPanel,
  CodeSetSelectorSection,
  CodeSetSelectorFolder,
  type CodeSetItem,
} from 'src/components/CodeSetSelector';
import type { AccountFolder } from 'src/api/redux/accountFolders';

/**
 * Interface for column item data structure
 */
interface ColumnItem {
  id: number;
  label: string;
  type: 'code_set' | 'cohort_id';
  scope?: 'PERSONAL' | 'COMPANY';
  owner?: {
    id: number;
    first_name: string;
    last_name: string;
    username: string;
  };
  filters?: Array<any>;
  created_at?: string;
  status?: string;
}

/**
 * Interface for the processed data result
 */
interface ProcessedData {
  personalColumns: ColumnItem[];
  companyColumns: ColumnItem[];
  personalFolders: AccountFolder[];
  companyFolders: AccountFolder[];
}

/**
 * ColumnSelectorDropdown component for selecting table columns from folders and standalone columns
 *
 * This component provides a dropdown interface for selecting columns that can be either
 * stored in folders (organized) or standalone. It supports both personal and company scoped
 * columns with proper filtering and organization.
 *
 * @returns React component for column selection dropdown
 *
 * @example
 * ```tsx
 * <ColumnSelectorDropdown />
 * ```
 */
interface ColumnSelectorDropdownProps {
  location?: string;
}

const ColumnSelectorDropdown: React.FC<ColumnSelectorDropdownProps> = ({
  location = 'lists',
}): React.ReactElement => {
  const {
    columnId,
    setColumnId,
    updateColumnId,
    mode, // Get current mode
    editingColumnId, // Get editing column ID to determine if we're in editing mode
  } = useTableFilterSelections(location);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [openAccordion, setOpenAccordion] = useState<number | null>(0);
  const [openFolders, setOpenFolders] = useState<{ [key: number]: boolean }>(
    {}
  );
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Shared data layer: fetch + folder detection + live-status resolution +
  // deactivated exclusion. See src/components/CodeSetSelector.
  const {
    ungroupedItems,
    getFolderItems,
    error: foldersError,
    raw: { accountFoldersData },
  } = useCodeSetSelectorData({ deactivatedPolicy: 'exclude' });

  // Map a normalized code-set item to this dropdown's ColumnItem shape.
  const toColumnItem = useCallback(
    (item: CodeSetItem): ColumnItem => ({
      id: item.id,
      label: item.label,
      type: item.kind === 'cohort' ? 'cohort_id' : 'code_set',
      scope: item.scope?.toUpperCase() as 'PERSONAL' | 'COMPANY',
      owner: item.raw?.owner,
      filters: item.raw?.filters || [],
      created_at: item.raw?.created_at || new Date().toISOString(),
      status: item.status,
    }),
    []
  );

  // Bucket ungrouped items + folders into personal/company for rendering.
  const {
    personalColumns,
    companyColumns,
    personalFolders,
    companyFolders,
  }: ProcessedData = useMemo(() => {
    const result: ProcessedData = {
      personalColumns: [],
      companyColumns: [],
      personalFolders: [],
      companyFolders: [],
    };

    accountFoldersData?.results?.forEach((folder: AccountFolder) => {
      if (folder.scope === 'PERSONAL') {
        result.personalFolders.push(folder);
      } else if (folder.scope === 'COMPANY') {
        result.companyFolders.push(folder);
      }
    });

    ungroupedItems.forEach((item) => {
      const column = toColumnItem(item);
      // Preserve original routing: cohorts fall back to company, filter sets to
      // personal, when the scope is unrecognized.
      if (item.kind === 'cohort') {
        if (item.scope?.toLowerCase() === 'personal') {
          result.personalColumns.push(column);
        } else {
          result.companyColumns.push(column);
        }
      } else if (item.scope?.toLowerCase() === 'company') {
        result.companyColumns.push(column);
      } else {
        result.personalColumns.push(column);
      }
    });

    return result;
  }, [accountFoldersData, ungroupedItems, toColumnItem]);

  /**
   * Get columns for a specific folder (scope filtering + live status resolution
   * + deactivated exclusion handled by the shared data hook).
   */
  const getFolderColumns = useCallback(
    (folder: AccountFolder): ColumnItem[] =>
      getFolderItems(folder).map(toColumnItem),
    [getFolderItems, toColumnItem]
  );

  // Find the selected column data
  const selectedColumn = useMemo((): ColumnItem | null => {
    if (!columnId) return null;

    // Extract base ID from composite key for comparison
    const baseColumnId = extractBaseId(columnId);

    // Check personal columns
    const personalColumn = personalColumns.find(
      (col) => col.id.toString() === baseColumnId
    );
    if (personalColumn) return personalColumn;

    // Check company columns
    const companyColumn = companyColumns.find(
      (col) => col.id.toString() === baseColumnId
    );
    if (companyColumn) return companyColumn;

    // Check folders
    const allFolders = [...personalFolders, ...companyFolders];
    for (const folder of allFolders) {
      const folderColumns = getFolderColumns(folder);
      const foundColumn = folderColumns.find(
        (col) => col.id.toString() === baseColumnId
      );
      if (foundColumn) return foundColumn;
    }

    return null;
  }, [
    columnId,
    personalColumns,
    companyColumns,
    personalFolders,
    companyFolders,
    getFolderColumns,
  ]);

  // Only use the outside click handler when dropdown is open
  useClickOutside([dropdownRef, triggerRef], () => setIsOpen(false), isOpen);

  /**
   * Toggle dropdown open/closed state
   *
   * @param event - Mouse event from trigger element
   */
  const toggleDropdown = useCallback((): void => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  /**
   * Toggle accordion section open/closed
   *
   * @param index - Index of the accordion section to toggle
   */
  const toggleAccordion = useCallback(
    (index: number): void => {
      setOpenAccordion(openAccordion === index ? null : index);
    },
    [openAccordion]
  );

  /**
   * Toggle folder open/closed state
   *
   * @param folderId - ID of the folder to toggle
   */
  const toggleFolder = useCallback((folderId: number): void => {
    setOpenFolders((prev) => ({
      ...prev,
      [folderId]: !prev[folderId],
    }));
  }, []);

  /**
   * Handle column selection - instance ID generation happens in Redux
   *
   * @param column - The column item to select
   */
  const handleSelect = useCallback(
    (column: ColumnItem): void => {
      const baseId = column.id.toString();

      // If we're in editing mode (editingColumnId exists), always update the existing column
      if (editingColumnId) {
        // We're editing an existing column, so update it with the new base ID
        // Redux will generate a new instance ID
        updateColumnId(baseId, column.type);
      } else {
        // We're creating a new column, so use setColumnId
        // Redux will generate an instance ID for us
        setColumnId(baseId, column.type);
      }

      setIsOpen(false);
    },
    [mode, editingColumnId, updateColumnId, setColumnId]
  );

  /**
   * Handle clearing the current selection
   */
  const handleClearSelection = useCallback((): void => {
    setColumnId(null);
    setIsOpen(false);
  }, [setColumnId]);

  /**
   * Handle keyboard navigation for dropdown trigger
   *
   * @param event - Keyboard event
   */
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>): void => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        const target = event.currentTarget as HTMLElement;
        const clickEvent = new MouseEvent('click', { bubbles: true });
        target.dispatchEvent(clickEvent);
      }
    },
    []
  );

  // Render folder component
  const renderFolder = useCallback(
    (folder: AccountFolder): React.ReactElement => (
      <CodeSetSelectorFolder
        key={folder.id}
        label={folder.label}
        isOpen={Boolean(openFolders[folder.id])}
        onToggle={() => toggleFolder(folder.id)}
        data-testid={`column-folder-${folder.id}`}
      >
        {getFolderColumns(folder)
          .sort((a, b) => a.label.localeCompare(b.label))
          .map((column) => (
            <CodeSetItemRow
              key={`folder-column-${column.id}`}
              label={column.label}
              kind={column.type === 'cohort_id' ? 'cohort' : 'filterSet'}
              status={column.status}
              isSelected={columnId === column.id.toString()}
              onSelect={() => handleSelect(column)}
              data-testid={`column-item-${column.id}`}
            />
          ))}
      </CodeSetSelectorFolder>
    ),
    [openFolders, getFolderColumns, toggleFolder, handleSelect, columnId]
  );

  // Show error state if data fetching failed
  if (foldersError) {
    return (
      <Box className="relative w-full">
        <Box className="w-full px-4 py-3 bg-red-50 border border-red-200 rounded-full">
          <Typography className="text-sm text-red-600">
            Error loading columns. Please try again.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="relative w-full">
      {/* Dropdown Trigger */}
      <Box
        ref={triggerRef}
        className="w-full px-4 py-3 bg-white flex items-center justify-between border border-neutral-300 rounded-full cursor-pointer hover:bg-neutral-50"
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={
          selectedColumn
            ? `Selected: ${selectedColumn.label}`
            : 'Select a column'
        }
        data-testid="column-selector-trigger"
      >
        <Box className="flex items-center gap-2">
          {selectedColumn ? (
            <Icon
              icon={faBookmark}
              className="w-4 h-4 text-neutral-600"
              size="sm"
              aria-hidden="true"
            />
          ) : (
            <Icon
              icon={faGlobe}
              className="w-4 h-4 text-neutral-600"
              aria-hidden="true"
            />
          )}
          <Typography className="text-sm text-neutral-800">
            {selectedColumn ? (
              <>
                {selectedColumn.label}
                {selectedColumn.type === 'cohort_id' && (
                  <CohortStatusLabel
                    status={selectedColumn.status as CohortStatus | undefined}
                  />
                )}
              </>
            ) : (
              'Select Column'
            )}
          </Typography>
        </Box>
        <Icon
          icon={faCaretDown}
          className={`text-neutral-600 w-4 h-4 transition-all duration-300
            ${isOpen ? '-rotate-90' : 'rotate-0'}
            `}
          aria-hidden="true"
        />
      </Box>

      {/* Dropdown Menu */}
      {isOpen && (
        <CodeSetSelectorPanel
          ref={dropdownRef}
          className="absolute top-full left-0 mt-1 w-full"
          role="listbox"
          aria-label="Available columns"
          data-testid="column-selector-dropdown"
          header={
            <Box className="flex items-center justify-between px-4 py-2 border-b border-neutral-300 shrink-0">
              <Box className="flex items-center gap-2">
                <Icon
                  icon={faFolderBookmark}
                  className="w-4 h-4 text-neutral-600"
                  aria-hidden="true"
                />
                <Typography className="text-sm font-bold text-neutral-800">
                  Available Columns
                </Typography>
              </Box>
            </Box>
          }
        >
          {/* Clear Selection Option */}
          <Box
            className="px-4 py-2 hover:bg-neutral-100 cursor-pointer"
            onClick={handleClearSelection}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClearSelection();
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="Clear column selection"
            data-testid="column-selector-clear-option"
          >
            <Typography className="text-sm text-neutral-800 font-semibold">
              Clear Selection
            </Typography>
          </Box>

          {/* Company Section */}
          {(companyFolders.length > 0 || companyColumns.length > 0) && (
            <CodeSetSelectorSection
              label="Company"
              isOpen={openAccordion === 0}
              onToggle={() => toggleAccordion(0)}
              data-testid="column-selector-company-accordion"
            >
              {companyFolders
                .sort((a, b) => a.label.localeCompare(b.label))
                .map(renderFolder)}
              {companyColumns
                .sort((a, b) => a.label.localeCompare(b.label))
                .map((column) => (
                  <CodeSetItemRow
                    key={`company-column-${column.id}`}
                    label={column.label}
                    kind={column.type === 'cohort_id' ? 'cohort' : 'filterSet'}
                    status={column.status}
                    isSelected={columnId === column.id.toString()}
                    onSelect={() => handleSelect(column)}
                    data-testid={`column-item-${column.id}`}
                  />
                ))}
            </CodeSetSelectorSection>
          )}

          {/* Personal Section */}
          {(personalFolders.length > 0 || personalColumns.length > 0) && (
            <CodeSetSelectorSection
              label="Personal"
              isOpen={openAccordion === 1}
              onToggle={() => toggleAccordion(1)}
              data-testid="column-selector-personal-accordion"
            >
              {personalFolders
                .sort((a, b) => a.label.localeCompare(b.label))
                .map(renderFolder)}
              {personalColumns
                .sort((a, b) => a.label.localeCompare(b.label))
                .map((column) => (
                  <CodeSetItemRow
                    key={`personal-column-${column.id}`}
                    label={column.label}
                    kind={column.type === 'cohort_id' ? 'cohort' : 'filterSet'}
                    status={column.status}
                    isSelected={columnId === column.id.toString()}
                    onSelect={() => handleSelect(column)}
                    data-testid={`column-item-${column.id}`}
                  />
                ))}
            </CodeSetSelectorSection>
          )}

          {/* No Columns State */}
          {personalFolders.length === 0 &&
            companyFolders.length === 0 &&
            personalColumns.length === 0 &&
            companyColumns.length === 0 && (
              <Box className="px-4 py-4 flex justify-center items-center">
                <Typography className="text-sm text-neutral-500">
                  No columns found
                </Typography>
              </Box>
            )}
        </CodeSetSelectorPanel>
      )}
    </Box>
  );
};

export { ColumnSelectorDropdown as default };
